import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, Check, ExternalLink, Info, Power, RefreshCw, UserCheck, X } from 'lucide-react'
import { toast } from 'sonner'
import { DashboardLayout } from '@/components/layout'
import { Card, CardContent } from '@/components/ui/card'
import { type AutomationMode, type StageKey, getApprovals, getAutomation, resolveApproval, runCoordinationScan, setAutomation } from '@/lib/api'
import { atsNav } from '@/lib/atsNav'
import { PageHeader } from '@/lib/atsUi'
import { currentTenant, currentUser } from '@/lib/mocks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/automation')({ component: AutomationPage })

// Display labels are Auto / Manual / Disable; the underlying API values stay auto/review/off (display-only).
const MODES: { v: AutomationMode; label: string; icon: typeof Bot; desc: string }[] = [
  { v: 'auto', label: 'Auto', icon: Bot, desc: 'runs automatically' },
  { v: 'review', label: 'Manual', icon: UserCheck, desc: 'runs, waits for your approval' },
  { v: 'off', label: 'Disable', icon: Power, desc: 'stays off' },
]

// Hover comparison of the three automation modes — what each does for a pipeline stage.
function ModeHelp() {
  return (
    <div className="pointer-events-none absolute right-0 top-full z-50 mt-1.5 hidden w-80 rounded-lg border border-border bg-card p-3 text-left shadow-lg group-hover:block">
      <div className="mb-1.5 text-xs font-medium text-foreground">What each mode does for this step</div>
      <table className="w-full border-collapse text-xs">
        <tbody className="align-top">
          <tr>
            <td className="w-[4.5rem] py-1 pr-2"><span className="inline-flex items-center gap-1 font-medium text-success"><Bot className="h-3.5 w-3.5" />Auto</span></td>
            <td className="py-1 text-muted-foreground">Agent does the step <span className="text-foreground">and commits / sends it</span> — fully hands-off.</td>
          </tr>
          <tr className="border-t border-border">
            <td className="py-1 pr-2"><span className="inline-flex items-center gap-1 font-medium text-info"><UserCheck className="h-3.5 w-3.5" />Manual</span></td>
            <td className="py-1 text-muted-foreground">Agent does all the work, then <span className="text-foreground">waits in your Approvals inbox</span> — nothing commits until you approve.</td>
          </tr>
          <tr className="border-t border-border">
            <td className="py-1 pr-2"><span className="inline-flex items-center gap-1 font-medium text-muted-foreground"><Power className="h-3.5 w-3.5" />Disable</span></td>
            <td className="py-1 text-muted-foreground">Agent <span className="text-foreground">doesn’t run</span> this step — you handle it yourself.</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
const stageBadge = (s: StageKey) => ({ jd_intake: 'JD', cv_ingest: 'Sourcing', screening: 'Screening', submission: 'Submission', feedback_chase: 'Feedback', scheduling: 'Schedule', warming: 'Warming' })[s] ?? s

function AutomationPage() {
  const qc = useQueryClient()
  const { data: auto } = useQuery({ queryKey: ['automation'], queryFn: getAutomation })
  const { data: appr } = useQuery({ queryKey: ['approvals', 'pending'], queryFn: () => getApprovals('pending') })

  const setMode = async (stage: StageKey, mode: AutomationMode) => {
    try {
      await setAutomation(stage, mode)
      qc.invalidateQueries({ queryKey: ['automation'] })
    } catch { toast.error('Could not update.') }
  }
  const resolve = async (id: string, decision: 'approved' | 'rejected') => {
    try {
      await resolveApproval(id, decision)
      qc.invalidateQueries({ queryKey: ['approvals'] })
      qc.invalidateQueries({ queryKey: ['jobs'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success(decision === 'approved' ? 'Approved — agent executed' : 'Dismissed')
    } catch { toast.error('Could not resolve.') }
  }
  const scanNow = async () => {
    try {
      const r = await runCoordinationScan()
      qc.invalidateQueries({ queryKey: ['approvals'] })
      toast.success(r.created > 0 ? `Coordination agent drafted ${r.created} follow-up${r.created === 1 ? '' : 's'}` : 'No stalled deals — all caught up')
    } catch { toast.error('Could not run the scan.') }
  }

  const tasks = appr?.tasks ?? []

  return (
    <DashboardLayout title="Automation" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader title="Automation" subtitle="Your operating system — set what runs on its own, review the rest. Agents do the work; you approve at the gates." />

      {/* Pipeline automation — the Auto / Review / Off dial per stage */}
      <h2 className="mt-5 mb-2 text-sm font-medium text-foreground">Pipeline automation</h2>
      <Card>
        <CardContent className="divide-y divide-border p-0">
          <div className="group relative flex items-center justify-end gap-1 px-4 py-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Review</span>
            <Info className="h-3 w-3 text-muted-foreground" />
            <ModeHelp />
          </div>
          {(auto?.stages ?? []).map((s) => (
            <div key={s.key} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div>
                <div className="text-sm font-medium text-foreground">{s.label}</div>
                <div className="text-xs text-muted-foreground">{MODES.find((m) => m.v === s.mode)?.desc}</div>
              </div>
              <div className="group relative">
                <div className="flex items-center rounded-md border border-border p-0.5">
                {MODES.map((m) => (
                  <button
                    key={m.v}
                    onClick={() => setMode(s.key, m.v)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs transition-colors',
                      s.mode === m.v
                        ? m.v === 'auto' ? 'bg-success/15 font-medium text-success'
                          : m.v === 'review' ? 'bg-info/15 font-medium text-info'
                            : 'bg-muted font-medium text-muted-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <m.icon className="h-3.5 w-3.5" /> {m.label}
                  </button>
                ))}
                </div>
                <ModeHelp />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Approvals inbox — the 20% human gate */}
      <div className="mt-6 mb-2 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-medium text-foreground">
          Needs your approval
          {tasks.length > 0 && <span className="rounded-full bg-info px-2 py-0.5 text-xs font-medium text-info-foreground">{tasks.length}</span>}
        </h2>
        <button onClick={scanNow} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted/50" title="Scan for stalled deals and draft follow-ups"><RefreshCw className="h-3.5 w-3.5" /> Run coordination</button>
      </div>
      <Card>
        <CardContent className="p-0">
          {tasks.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">Nothing waiting. Agents will queue items here when a stage is set to <span className="font-medium text-info">Manual</span>.</p>
          ) : (
            <div className="divide-y divide-border">
              {tasks.map((t) => (
                <div key={t.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="rounded border border-border bg-muted/50 px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">{stageBadge(t.stage)}</span>
                        <span className="text-sm font-medium text-foreground">{t.title}</span>
                      </div>
                      {t.summary && <div className="mt-0.5 truncate text-xs text-muted-foreground">{t.summary}</div>}
                      <Preview task={t} />
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      <button onClick={() => resolve(t.id, 'approved')} className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"><Check className="h-3.5 w-3.5" /> Approve</button>
                      <button onClick={() => resolve(t.id, 'rejected')} className="inline-flex items-center gap-1 rounded-md border border-input bg-card px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted/50"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  )
}

// A compact preview of the agent's drafted artifact (so you can approve with confidence).
function Preview({ task }: { task: { stage: StageKey; payload: unknown } }) {
  const p = task.payload as Record<string, any> | null
  if (!p) return null
  // client responded via a share-link
  if (p.clientResponse) {
    const r = p.clientResponse
    return (
      <div className="mt-1.5 rounded-md border border-success/30 bg-success/5 p-2 text-xs">
        {Array.isArray(r.verdicts) && r.verdicts.map((v: any) => (
          <span key={v.handle} className="mr-2">{v.handle}: <b className={v.decision === 'advance' ? 'text-success' : 'text-destructive'}>{v.decision}</b></span>
        ))}
        {r.slots && <div className="text-foreground">Proposed slots: {r.slots}</div>}
        {r.note && <div className="mt-0.5 italic text-muted-foreground">“{r.note}”</div>}
      </div>
    )
  }
  // coordination drafted touch (chase / schedule / warm)
  if (task.stage === 'feedback_chase' || task.stage === 'scheduling' || task.stage === 'warming') {
    return (
      <div className="mt-1.5 rounded-md border border-border bg-muted/30 p-2 text-xs">
        <div className="whitespace-pre-line text-muted-foreground">{String(p.message ?? '').slice(0, 240)}</div>
        {p.shareToken && <a href={`/s/${p.shareToken}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-info hover:underline"><ExternalLink className="h-3 w-3" /> Preview client link</a>}
      </div>
    )
  }
  if (task.stage === 'submission' && Array.isArray(p.candidates)) {
    return (
      <div className="mt-1.5 rounded-md border border-border bg-muted/30 p-2 text-xs">
        <div className="mb-1 whitespace-pre-line text-muted-foreground">{String(p.coverNote ?? '').split('\n').slice(0, 2).join(' ')}</div>
        {p.candidates.slice(0, 4).map((c: any) => (
          <div key={c.handle} className="text-foreground"><span className="font-medium">{c.handle}</span> · {c.headline} · <span className="tabular-nums">fit {c.fit}</span></div>
        ))}
      </div>
    )
  }
  if (task.stage === 'jd_intake') {
    const must: string[] = Array.isArray(p.mustHave) ? p.mustHave : []
    const nice: string[] = Array.isArray(p.niceToHave) ? p.niceToHave : []
    return (
      <div className="mt-1.5 flex flex-wrap gap-1">
        {must.map((s) => <span key={s} className="rounded border border-info/40 bg-info/10 px-1.5 py-0.5 text-[11px] text-info">{s}</span>)}
        {nice.map((s) => <span key={s} className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[11px] text-muted-foreground">{s}</span>)}
      </div>
    )
  }
  return null
}
